/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 * Updated: 2026-04-12 — replaced DataGridPro with pure CSS Grid (feat/event/mr-14)
 *
 * Pure CSS Grid slot table. Replaces the previous DataGridPro implementation.
 * Each data cell carries data-hour and data-court-id attributes so that
 * useDragSelection can identify cells without MUI internals.
 */

import { CircularProgress, Tooltip } from '@mui/material'
import { GAME_FORMAT_CHIP } from '@tc-app/shared/labels'
import type { SpanBlock } from '@tc-app/shared/slots/mergeSlots'
import { SlotStatus } from '@tc-app/shared/slots/types'
import { Fragment } from 'react'
import styles from './SlotGrid.module.css'
import type { SlotCoachingMeta, SlotMeta } from './types'

function formatHour(h: number) {
  return `${h.toString().padStart(2, '0')}:00`
}

interface Court {
  id: string
  name: string
  displayOrder: number
}

interface SlotGridProps {
  courts: Court[]
  /** Ordered list of hours to render, derived from the slots query response */
  hours: number[]
  slotLookup: Map<string, SlotMeta>
  /** Cells covered by a span block — rendered by the span layer instead */
  coveredCells: Set<string>
  /** Span blocks to render as merged cells */
  spanBlocks: SpanBlock<SlotMeta>[]
  pastHourThreshold: number
  selectedKeys: Set<string>
  loading: boolean
  gridRef: React.RefObject<HTMLDivElement | null>
  onMouseDown: (e: React.MouseEvent) => void
  onMouseMove: (e: React.MouseEvent) => void
  onMouseUp: (e: React.MouseEvent) => void
  onCellClick: (hour: number, courtId: string) => void
}

export default function SlotGrid({
  courts,
  hours,
  slotLookup,
  coveredCells,
  spanBlocks,
  pastHourThreshold,
  selectedKeys,
  loading,
  gridRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onCellClick,
}: SlotGridProps) {
  // CSS Grid: 1 time col + 1 col per court, each 100px wide
  const gridTemplateColumns = `80px repeat(${courts.length}, 100px)`

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-selection container needs mouse events
    <div
      ref={gridRef}
      className={styles.wrapper}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <CircularProgress />
        </div>
      )}

      <div className={styles.grid} style={{ gridTemplateColumns }}>
        {/* ── Header row ── */}
        <div className={styles.headerCell}>时段</div>
        {courts.map((c) => (
          <div key={c.id} className={styles.headerCell}>
            {c.name}
          </div>
        ))}

        {/* ── Span blocks (merged cells) ── */}
        {spanBlocks.map((block) => {
          // grid rows: header=1, first hour → row 2, nth hour → row n+2
          const gridRowStart = hours.indexOf(block.hourStart) + 2
          const gridColStart = block.courtIndex + 2 // time col is col 1
          const isClickable =
            !!block.meta.eventId ||
            !!block.meta.sessionId ||
            (block.meta.status === SlotStatus.Booked && !!block.meta.orderId)
          const statusClass = block.meta.coaching
            ? coachingStatusClass(block.meta.coaching)
            : block.meta.status === SlotStatus.Booked
              ? styles.statusBooked
              : block.meta.status === SlotStatus.Locked
                ? styles.statusLocked
                : block.meta.eventId
                  ? styles.statusReservedEvent
                  : styles.statusReserved
          const spanKey = `${block.hourStart}-${courts[block.courtIndex].id}`
          const isSelected = selectedKeys.has(spanKey)
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: grid cell is conditionally interactive
            <div
              key={`span-${block.courtIndex}-${block.hourStart}`}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              className={`${styles.cell} ${styles.span} ${statusClass}${isSelected ? ` ${styles.selected}` : ''}`}
              style={{
                gridRow: `${gridRowStart} / span ${block.rowSpan}`,
                gridColumn: `${gridColStart} / span ${block.colSpan}`,
                height: 'auto',
                cursor: isClickable ? 'pointer' : undefined,
              }}
              data-hour={block.hourStart}
              data-court-id={courts[block.courtIndex].id}
              onClick={() => {
                if (isClickable) {
                  onCellClick(block.hourStart, courts[block.courtIndex].id)
                }
              }}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  onCellClick(block.hourStart, courts[block.courtIndex].id)
                }
              }}
            >
              <SlotCellContent meta={block.meta} />
            </div>
          )
        })}

        {/* ── Data rows ── */}
        {hours.map((hour) => (
          <Fragment key={hour}>
            {/* Time label */}
            <div className={styles.timeCell}>{formatHour(hour)}</div>

            {/* Court cells */}
            {courts.map((court) => {
              const key = `${hour}-${court.id}`
              if (coveredCells.has(key)) return null

              const meta = slotLookup.get(key)
              const isPast = hour < pastHourThreshold
              const isUnavailable = !meta

              const isBookedOrReserved =
                meta?.status === SlotStatus.Booked ||
                meta?.status === SlotStatus.Reserved

              const classNames = [styles.cell]
              if (isPast && !isBookedOrReserved) classNames.push(styles.past)
              else if (isUnavailable) classNames.push(styles.unavailable)
              else {
                if (meta.coaching) classNames.push(coachingStatusClass(meta.coaching))
                else if (meta.status === SlotStatus.Reserved)
                  classNames.push(
                    meta.eventId ? styles.statusReservedEvent : styles.statusReserved,
                  )
                else if (meta.status === SlotStatus.Booked)
                  classNames.push(styles.statusBooked)
                else if (meta.status === SlotStatus.Locked)
                  classNames.push(styles.statusLocked)
              }
              if (selectedKeys.has(key)) classNames.push(styles.selected)

              const isClickable = (!isPast || isBookedOrReserved) && !!meta
              return (
                // biome-ignore lint/a11y/noStaticElementInteractions: grid cell is conditionally interactive
                <div
                  key={key}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  className={classNames.join(' ')}
                  data-hour={hour}
                  data-court-id={court.id}
                  onClick={() => {
                    if (isClickable) onCellClick(hour, court.id)
                  }}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      onCellClick(hour, court.id)
                    }
                  }}
                >
                  {meta && (!isPast || isBookedOrReserved) && (
                    <SlotCellContent meta={meta} />
                  )}
                </div>
              )
            })}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

/**
 * Render the content of a slot cell (price + optional reason/event info).
 * Booked and reserved slots omit the price.
 */
export function SlotCellContent({ meta }: { meta: SlotMeta }) {
  if (meta.coaching) return <CoachingCellContent coaching={meta.coaching} />

  // For event slots, the format label is derived from `event.gameFormat` in
  // the UI rather than read off the slot's `reason` text.
  const gameFormatLabel = meta.event
    ? GAME_FORMAT_CHIP[meta.event.gameFormat].label
    : undefined
  // Reserved (non-event) slots show up to 2 lines of the reason text, since
  // there's no price taking up space in that cell; other statuses get 1 line.
  const reasonLine =
    gameFormatLabel ??
    (meta.status === SlotStatus.Reserved && !meta.event
      ? meta.reason?.split('\n').slice(0, 2).join('\n')
      : meta.reason?.split('\n')[0])
  const ratingRange =
    meta.event?.minRating != null && meta.event?.maxRating != null
      ? `${meta.event.minRating.toFixed(1)}-${meta.event.maxRating.toFixed(1)}`
      : undefined
  const signupProgress = meta.event
    ? `${meta.event.numSignups}/${meta.event.drawSize ?? '-'}`
    : undefined
  // Event slots display the event's signup fee, not the underlying court
  // price (`meta.price`) — the two can differ (e.g. free events on paid courts).
  // Booked/reserved slots show no price: a merged block only carries the first
  // slot's meta, so its price wouldn't reflect what was actually paid (#52).
  const displayPrice = meta.event
    ? meta.event.fee
    : meta.status === SlotStatus.Booked || meta.status === SlotStatus.Reserved
      ? undefined
      : meta.price

  const content = (
    <div style={{ width: '100%' }}>
      {meta.status === SlotStatus.Booked && (
        <div className={styles.cellCoaching}>订场</div>
      )}
      {displayPrice != null && <div className={styles.cellPrice}>¥{displayPrice}</div>}
      {reasonLine && (
        <div
          className={
            meta.status === SlotStatus.Reserved && !meta.event
              ? styles.cellReasonMultiline
              : styles.cellReason
          }
        >
          {reasonLine}
        </div>
      )}
      {ratingRange && <div className={styles.cellEventInfo}>{ratingRange}</div>}
      {signupProgress && <div className={styles.cellEventInfo}>{signupProgress}</div>}
    </div>
  )

  const tooltipText = gameFormatLabel ?? meta.reason
  if (tooltipText) {
    return (
      <Tooltip
        title={tooltipText}
        arrow
        placement="top"
        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
      >
        {content}
      </Tooltip>
    )
  }
  return content
}

/**
 * Green (as a booked slot) only once every attendee has confirmed; until then
 * the slot is styled as a plain reservation. An appointment with no attendees
 * yet (ENROLL before the first enrollment) is not confirmed.
 */
function coachingStatusClass(coaching: SlotCoachingMeta) {
  const allConfirmed =
    coaching.attendees.length > 0 &&
    coaching.attendees.every((a) => a.status === 'CONFIRMED')
  return allConfirmed ? styles.statusBooked : styles.statusReserved
}

/**
 * A slot reserved for a coaching appointment: "课程", then the student's
 * name for a 1:1 course, or confirmed/max for a group course.
 * When all attendees are confirmed, the second line uses a contrasting color
 * (matching the booking slot style).
 */
function CoachingCellContent({ coaching }: { coaching: SlotCoachingMeta }) {
  const confirmed = coaching.attendees.filter((a) => a.status === 'CONFIRMED')
  const allConfirmed =
    coaching.attendees.length > 0 &&
    coaching.attendees.every((a) => a.status === 'CONFIRMED')
  const detail =
    coaching.seats === 1
      ? (coaching.attendees[0]?.name ?? '')
      : `${confirmed.length}/${coaching.seats}`
  const tooltip = coaching.attendees
    .map((a) => `${a.name ?? '未命名'}${a.status === 'CONFIRMED' ? '' : '（待确认）'}`)
    .join('\n')

  const content = (
    <div style={{ width: '100%' }}>
      <div className={styles.cellCoaching}>课程</div>
      {detail && (
        <div className={allConfirmed ? styles.cellReason : styles.cellCoaching}>
          {detail}
        </div>
      )}
    </div>
  )
  if (!tooltip) return content
  return (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
    >
      {content}
    </Tooltip>
  )
}
